import * as t from 'io-ts';

import {makeEnumRuntimeType} from '../../../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {RAccountIdRef, RCardGroupIdRef, RCardIdRef} from '../../../../../models/api/apiTypes';
import {CrmSyncSourcesEnum} from '../../../../../models/api/crmSyncModel';

/*
 * Runtime types.
 */

export enum ContactsManagerParametersTypesEnum {
  ACCOUNTS = 'ACCOUNTS',
  ACCOUNTS_CSV_IMPORT = 'ACCOUNTS_CSV_IMPORT',
  ACCOUNT_CONTACTS = 'ACCOUNT_CONTACTS',
  ACCOUNT_SYNCS = 'ACCOUNT_SYNCS',

  COMPANY_CONTACTS = 'COMPANY_CONTACTS',
  PRIVATE_CONTACTS = 'PRIVATE_CONTACTS',
  OTHER_CONTACTS = 'OTHER_CONTACTS',

  LISTS = 'LISTS',
  LIST_CONTACTS = 'LIST_CONTACTS',

  SYNCS = 'SYNCS', // full page sync

  SEARCH = 'search',
}
const RContactsManagerParametersType = makeEnumRuntimeType(ContactsManagerParametersTypesEnum);

export enum ContactsManagerSearchTabsEnum {
  CONTACTS = 'CONTACTS',
  ACCOUNTS = 'ACCOUNTS',
  CARD_GROUPS = 'CARD_GROUPS',
}

export const RContactsManagerSearchTab = makeEnumRuntimeType(ContactsManagerSearchTabsEnum);
export const RCrmSyncSources = makeEnumRuntimeType(CrmSyncSourcesEnum);

/*
 * Base.
 */

export const RContactsManagerRouteLocation = t.type({
  pathname: t.string,
});

const RContactsManagerParametersBase = t.type({
  type: RContactsManagerParametersType,
  location: RContactsManagerRouteLocation,
});

/*
 * Page parameters.
 */

const RContactsManagerAccountParameters = t.intersection([
  RContactsManagerParametersBase,
  t.type({
    type: t.literal(ContactsManagerParametersTypesEnum.ACCOUNTS),
  }),
  t.partial({
    accountId: RAccountIdRef,
  }),
]);

const RContactsManagerAccountCsvImportParameters = t.intersection([
  RContactsManagerParametersBase,
  t.type({
    type: t.literal(ContactsManagerParametersTypesEnum.ACCOUNTS_CSV_IMPORT),
  }),
]);

const RContactsManagerAccountContactsParameters = t.intersection([
  RContactsManagerParametersBase,
  t.type({
    type: t.literal(ContactsManagerParametersTypesEnum.ACCOUNT_CONTACTS),
    accountId: RAccountIdRef,
  }),
  t.partial({
    cardId: RCardIdRef,
  }),
]);

const RContactsManagerSyncParameters = t.intersection([
  RContactsManagerParametersBase,
  t.type({
    type: t.literal(ContactsManagerParametersTypesEnum.SYNCS),
    source: RCrmSyncSources,
  }),
  t.partial({
    shouldShowErrors: t.boolean,
  }),
]);

const RContactsManagerAccountSyncParameters = t.intersection([
  RContactsManagerParametersBase,
  t.type({
    type: t.literal(ContactsManagerParametersTypesEnum.ACCOUNT_SYNCS),
    source: RCrmSyncSources,
  }),
  t.partial({
    shouldShowErrors: t.boolean,
  }),
]);

const RContactsManagerCompanyContactsParameters = t.intersection([
  RContactsManagerParametersBase,
  t.type({
    type: t.literal(ContactsManagerParametersTypesEnum.COMPANY_CONTACTS),
  }),
  t.partial({
    cardId: RCardIdRef,
  }),
]);

const RContactsManagerOtherContactsParameters = t.intersection([
  RContactsManagerParametersBase,
  t.type({
    type: t.literal(ContactsManagerParametersTypesEnum.OTHER_CONTACTS),
  }),
  t.partial({
    cardId: RCardIdRef,
  }),
]);

const RContactsManagerPrivateContactsParameters = t.intersection([
  RContactsManagerParametersBase,
  t.type({
    type: t.literal(ContactsManagerParametersTypesEnum.PRIVATE_CONTACTS),
    namespace: t.string,
  }),
  t.partial({
    cardId: RCardIdRef,
  }),
]);

const RContactsManagerListsParameters = t.intersection([
  RContactsManagerParametersBase,
  t.type({
    type: t.literal(ContactsManagerParametersTypesEnum.LISTS),
  }),
]);

const RContactsManagerListContactsParameters = t.intersection([
  RContactsManagerParametersBase,
  t.type({
    type: t.literal(ContactsManagerParametersTypesEnum.LIST_CONTACTS),
    cardGroupId: RCardGroupIdRef,
  }),
  t.partial({
    cardId: RCardIdRef,
  }),
]);

const RContactsManagerSearchParameters = t.intersection([
  RContactsManagerParametersBase,
  t.type({
    type: t.literal(ContactsManagerParametersTypesEnum.SEARCH),
    query: t.string,
    tab: t.union([t.undefined, RContactsManagerSearchTab]),
  }),
]);

/*
 * Union of parameters.
 */

export const RContactsManagerParameters = t.taggedUnion('type', [
  RContactsManagerAccountParameters,
  RContactsManagerAccountCsvImportParameters,
  RContactsManagerAccountContactsParameters,
  RContactsManagerCompanyContactsParameters,
  RContactsManagerOtherContactsParameters,
  RContactsManagerPrivateContactsParameters,
  RContactsManagerListsParameters,
  RContactsManagerListContactsParameters,
  RContactsManagerSearchParameters,
  RContactsManagerAccountSyncParameters,
  RContactsManagerSyncParameters,
]);

/*
 * Types.
 */

export interface ContactsManagerAccountParameters
  extends Readonly<t.TypeOf<typeof RContactsManagerAccountParameters>> {}

export interface ContactsManagerAccountCsvImportParamaters
  extends Readonly<t.TypeOf<typeof RContactsManagerAccountCsvImportParameters>> {}

export interface ContactsManagerAccountContactsParameters
  extends Readonly<t.TypeOf<typeof RContactsManagerAccountContactsParameters>> {}

export interface ContactsManagerCompanyContactsParameters
  extends Readonly<t.TypeOf<typeof RContactsManagerCompanyContactsParameters>> {}

export interface ContactsManagerOtherContactsParameters
  extends Readonly<t.TypeOf<typeof RContactsManagerOtherContactsParameters>> {}

export interface ContactsManagerPrivateContactsParameters
  extends Readonly<t.TypeOf<typeof RContactsManagerPrivateContactsParameters>> {}

export interface ContactsManagerListsParameters
  extends Readonly<t.TypeOf<typeof RContactsManagerListsParameters>> {}

export interface ContactsManagerListContactsParameters
  extends Readonly<t.TypeOf<typeof RContactsManagerListContactsParameters>> {}

export interface ContactsManagerSearchParameters
  extends Readonly<t.TypeOf<typeof RContactsManagerSearchParameters>> {}

export interface ContactsManagerAccountSyncParameters
  extends Readonly<t.TypeOf<typeof RContactsManagerAccountSyncParameters>> {}

export interface ContactsManagerSyncParameters
  extends Readonly<t.TypeOf<typeof RContactsManagerSyncParameters>> {}

export type ContactsManagerParameters =
  | ContactsManagerAccountParameters
  | ContactsManagerAccountCsvImportParamaters
  | ContactsManagerAccountContactsParameters
  | ContactsManagerCompanyContactsParameters
  | ContactsManagerOtherContactsParameters
  | ContactsManagerPrivateContactsParameters
  | ContactsManagerListsParameters
  | ContactsManagerListContactsParameters
  | ContactsManagerSearchParameters
  | ContactsManagerSyncParameters
  | ContactsManagerAccountSyncParameters;
